import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useGenericStrictContext } from 'utils/createContext'
import { CIMSTableProps } from '../types'
import { ColumnType, ColumnsType } from 'antd/lib/table'
import { useProminentCustomFields } from '../../CustomFields/hooks/useProminentCustomFields'
import { ColumnGroupType } from 'antd/lib/table/interface'
import { CIMSColumn } from '../CIMSColumn/types'
import CIMSTableCell from './components/CIMSTableCell'
import { useCIMSModalValues } from '../../CIMSModal/hooks/useCIMSModal'
import SimpleEditModal from '../components/SimpleEditModal'
import SimpleCreateModal from '../components/SimpleCreateModal'
import { CIMSTableContext } from './types'
import { useExpandableSection } from './hooks/useExpandableSection'
import { setSortOptions } from './utils/setSortOptions'
import { useTableFilter } from 'hooks/useTableFilter'
import { CIMSTag } from '../../CIMSTag'
import { removalTooltip } from '../../../utils/removalTooltip'
import { createBaseColumn } from './hooks/createBaseColumn'
import { useActiveColumnFn } from './hooks/useActiveColumnFn'
import { useActionsColumnFn } from './hooks/useActionsColumnFn'
import { useMiscellaneousHeaderActions } from './hooks/useMiscellaneousHeaderActions'
import { useGetWidth } from './hooks/useGetWidth'
import { value } from './utils/value'

const Context = createContext(null)

export function useCIMSTableContext<T extends object, U = any>() {
  return useGenericStrictContext<Readonly<CIMSTableContext<T, U>>>(
    Context,
    'CIMSTableContext'
  )
}

function CIMSTableProvider<RecordType extends object, InitialValues = any>({
  rowKey,
  records,
  actions,
  children,
  defaultSort,
  expand,
  disable,
  ...props
}: React.PropsWithChildren<CIMSTableProps<RecordType, InitialValues>>) {
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setLoading(props.loading ?? false)
  }, [props.loading])

  const tableFilter = useTableFilter(records)
  const getWidth = useGetWidth(records)
  const restore = actions?.row?.restore
  const toggleRemoved = restore?.display === 'toggle'
  /*
   * I cannot find a way to move this callback into it's own hook without
   * causing a max-depth error in React.
   *
   * I suspect it's because we're doing recursion for column groups and I can't
   * find the right way to set up the use callback dependencies.
   */
  const toColumn = useCallback<
    (
      column: CIMSColumn<RecordType>,
      removedLabelColumn?: CIMSColumn<RecordType>
    ) => ColumnsType<RecordType>[number]
  >(
    (configuredColumn, removedLabelColumn) => {
      const { filter, ...initial } = configuredColumn
      let column
      if (initial.type === 'group') {
        const columns = initial.columns
        column = {
          ...createBaseColumn(initial),
          children: columns.map((column) =>
            toColumn(column, removedLabelColumn)
          ),
        } as ColumnGroupType<RecordType>
      } else {
        const { render, align, sort, width } = initial

        column = {
          ...createBaseColumn(initial),
          align,
          width: getWidth(width),
          render: (value, record, index) => {
            const content =
              typeof render === 'function' ? render(record, index) : value
            const removalMetadata = toggleRemoved
              ? restore.metadata(record)
              : undefined
            const removed = removalMetadata?.removed === true
            const tooltip = removalMetadata
              ? removalTooltip(removalMetadata)
              : undefined
            return (
              <CIMSTableCell>
                {removed ? (
                  <>
                    <div style={{ textDecoration: 'line-through' }}>
                      {content}
                    </div>
                    {configuredColumn === removedLabelColumn && (
                      <CIMSTag
                        style={{ marginTop: 4 }}
                        color="red"
                        tabIndex={tooltip ? 0 : undefined}
                        tooltip={tooltip}
                      >
                        Removed
                      </CIMSTag>
                    )}
                  </>
                ) : (
                  content
                )}
              </CIMSTableCell>
            )
          },
        } as ColumnType<RecordType>
        if (typeof render !== 'function') {
          column.dataIndex = render
          column.key = render
        }
        if (sort) setSortOptions(column, sort)
      }

      if (filter) Object.assign(column, tableFilter(filter))

      return column
    },
    [getWidth, tableFilter, restore, toggleRemoved]
  )
  const activeColumn = useActiveColumnFn(actions)
  const actionsColumn = useActionsColumnFn({ records, rowKey, actions })

  const [sourceRecords, setDataSource] = useState<RecordType[]>([])
  const hasRemovedRecords =
    toggleRemoved &&
    sourceRecords.some((record) => restore.metadata(record).removed)
  const [showRemovedRecords, setShowRemovedRecords] = useState(false)
  if (!hasRemovedRecords && showRemovedRecords) {
    setShowRemovedRecords(false)
  }

  const dataSource = useMemo(
    () =>
      toggleRemoved && !showRemovedRecords
        ? sourceRecords.filter((record) => !restore.metadata(record).removed)
        : sourceRecords,
    [sourceRecords, toggleRemoved, showRemovedRecords, restore]
  )
  const { expandable, showCollapseButton, setExpandedRowKeys } =
    useExpandableSection({ dataSource, rowKey, expand })
  const miscHeaderActions = useMiscellaneousHeaderActions({
    records,
    rowKey,
    setDataSource,
    setExpandedRowKeys,
    actions,
    defaultSort,
  })
  const prominent = useProminentCustomFields(
    Array.isArray(props.columns)
      ? {
          records: [],
          disableEditWhen: () => ({ alreadyDisabled: false }),
          onCheckBoolean: () => {},
          onCheckBooleans: () => {},
        }
      : { ...props.columns.prominentCustomFields, records }
  )

  const { state: editState, ...simpleEdit } = useCIMSModalValues<RecordType>()
  const { state: _ignored, ...simpleCreate } = useCIMSModalValues<null>()
  const configuredColumns = [
    ...(Array.isArray(props.columns) ? props.columns : props.columns.columns),
    ...prominent,
  ]
  const removedLabelColumn = configuredColumns.flatMap((column) =>
    column.type === 'group' ? column.columns : [column]
  )[0]
  return (
    <Context.Provider
      value={
        value({
          disable,
          modals: { simpleEdit, simpleCreate },
          columns: activeColumn(
            actionsColumn(
              configuredColumns.map((column) =>
                toColumn(column, removedLabelColumn)
              )
            )
          ),
          dataSource,
          loading,
          setLoading,
          expandable,
          rowKey,
          actions: {
            row: actions?.row,
            header: {
              ...actions?.header,
              ...miscHeaderActions,
              removed: {
                visible: hasRemovedRecords,
                showing: showRemovedRecords,
                onToggle: () => {
                  setExpandedRowKeys([])
                  setShowRemovedRecords((showing) => !showing)
                },
              },
              collapse: {
                visible: showCollapseButton,
                onCollapse: () => setExpandedRowKeys([]),
              },
            },
          },
          // we're doing this on purpose because you can't use createStrictContext with generics
        }) as unknown as null
      }
    >
      <SimpleEditModal
        onClose={() => simpleEdit.toggle(null)}
        visible={simpleEdit.visible}
        state={editState}
      />
      <SimpleCreateModal
        onClose={() => simpleCreate.toggle(null)}
        visible={simpleCreate.visible}
      />
      {children}
    </Context.Provider>
  )
}

export default CIMSTableProvider
